import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    Typography,
    Button,
    CircularProgress,
    Alert,
    Stack,
    Modal,
    ModalDialog,
    Textarea,
    FormLabel,
    Sheet,
    Table
} from '@mui/joy';
import axiosInstance from './util/axiosConfig';

const tableSheetSx = {
    width: '100%',
    maxWidth: '100%',
    borderRadius: 'sm',
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: 400,
    WebkitOverflowScrolling: 'touch',
    border: '1px solid',
    borderColor: 'divider'
};

const tableBaseSx = {
    width: 'max-content',
    minWidth: '100%',
    tableLayout: 'fixed',
    '& tbody tr:nth-of-type(even)': {
        backgroundColor: '#f9f9f9'
    },
    '& tbody tr:hover': {
        backgroundColor: '#fff5ed'
    },
    '& thead': {
        position: 'sticky',
        top: 0,
        zIndex: 10
    },
    '& thead th': {
        backgroundColor: '#eb7a24',
        color: 'white',
        fontWeight: 'bold',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        borderBottom: '2px solid #e55125',
        whiteSpace: 'nowrap'
    },
    '& td, & th': {
        padding: '12px',
        border: '1px solid',
        borderColor: 'divider',
        verticalAlign: 'top',
        overflow: 'hidden'
    }
};

const onlineCampusTableSx = {
    ...tableBaseSx,
    '& th:nth-of-type(1), & td:nth-of-type(1)': { width: 80, minWidth: 80, whiteSpace: 'nowrap' },
    '& th:nth-of-type(2), & td:nth-of-type(2)': { width: 130, minWidth: 130, whiteSpace: 'nowrap' },
    '& th:nth-of-type(3), & td:nth-of-type(3)': { width: 220, minWidth: 220, whiteSpace: 'normal', wordBreak: 'break-word' },
    '& th:nth-of-type(4), & td:nth-of-type(4)': { width: 260, minWidth: 260, whiteSpace: 'normal' }
};

const confirmedCampusTableSx = {
    ...tableBaseSx,
    '& th:nth-of-type(1), & td:nth-of-type(1)': { width: 80, minWidth: 80, whiteSpace: 'nowrap' },
    '& th:nth-of-type(2), & td:nth-of-type(2)': { width: 130, minWidth: 130, whiteSpace: 'nowrap' },
    '& th:nth-of-type(3), & td:nth-of-type(3)': { width: 200, minWidth: 200, whiteSpace: 'normal', wordBreak: 'break-word' },
    '& th:nth-of-type(4), & td:nth-of-type(4)': { width: 120, minWidth: 120, whiteSpace: 'nowrap' },
    '& th:nth-of-type(5), & td:nth-of-type(5)': { width: 220, minWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word' }
};

const cellTextSx = {
    whiteSpace: 'inherit',
    wordBreak: 'inherit'
};

const CampusConfirmation = () => {
    const [loading, setLoading] = useState(true);
    const [confirming, setConfirming] = useState(false);
    const [notInterestedLoading, setNotInterestedLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [campusData, setCampusData] = useState(null);
    const [confirmedCampuses, setConfirmedCampuses] = useState([]);
    const [onlineCampuses, setOnlineCampuses] = useState([]);
    const [reasonModalOpen, setReasonModalOpen] = useState(false);
    const [selectedCampusId, setSelectedCampusId] = useState(null);
    const [notInterestedReason, setNotInterestedReason] = useState('');
    const [confirmedPage, setConfirmedPage] = useState(1);
    const [onlinePage, setOnlinePage] = useState(1);
    const perPage = 50;
    const [pagination, setPagination] = useState({
        confirmed: { page: 1, limit: perPage, total: 0 },
        online: { page: 1, limit: perPage, total: 0 }
    });

    // Initialize data on component mount
    useEffect(() => {
        initData();
    }, [confirmedPage, onlinePage]);

    const initData = async () => {
        try {
            setLoading(true);
            setError(null);

            // Fetch campus data from the new API
            const response = await axiosInstance.get('/students/student-campuses', {
                params: {
                    confirmedPage: confirmedPage,
                    confirmedLimit: perPage,
                    onlinePage: onlinePage,
                    onlineLimit: perPage
                }
            });

            if (response.data.errorMessage) {
                throw new Error(response.data.errorMessage);
            }

            setCampusData(response.data);

            // Set confirmed and online campuses from response
            if (response.data.confirmedCampuses) {
                setConfirmedCampuses(response.data.confirmedCampuses);
            }
            if (response.data.onlineCampuses) {
                setOnlineCampuses(response.data.onlineCampuses);
            }
            if (response.data.pagination) {
                setPagination(response.data.pagination);
            } else if (response.data.summary) {
                setPagination({
                    confirmed: { page: confirmedPage, limit: perPage, total: response.data.summary.totalConfirmed || 0 },
                    online: { page: onlinePage, limit: perPage, total: response.data.summary.totalPendingConfirmation || 0 }
                });
            }

        } catch (err) {
            console.error('Error fetching campus data:', err);
            setError(err.message || 'Failed to load campus data');
        } finally {
            setLoading(false);
        }
    };

    const confirmCampus = async (campusId) => {
        if (!campusId) {
            setError('Please select a campus first');
            return;
        }

        try {
            setConfirming(true);
            setError(null);
            setSuccessMessage(null);

            // Call the confirmation API
            const response = await axiosInstance.post('/students/confirm-campus', {
                campusId: campusId
            });

            if (response.data.success) {
                setSuccessMessage('Campus confirmed successfully!');
                // Refresh data after successful confirmation
                await initData();
            } else {
                throw new Error(response.data.errorMessage || 'Failed to confirm campus');
            }

        } catch (err) {
            console.error('Error confirming campus:', err);
            setError(err.message || 'Failed to confirm campus');
            // Still refresh data even on error
            await initData();
        } finally {
            setConfirming(false);
        }
    };

    const handleNotInterestedClick = (campusId) => {
        setSelectedCampusId(campusId);
        setNotInterestedReason('');
        setReasonModalOpen(true);
    };

    const handleNotInterestedSubmit = async () => {
        if (!selectedCampusId) {
            setError('Please select a campus first');
            return;
        }

        try {
            setNotInterestedLoading(true);
            setError(null);
            setSuccessMessage(null);

            // Call the not interested API
            const response = await axiosInstance.post('/students/not-interested-campus', {
                campusId: selectedCampusId,
                reason: notInterestedReason || null
            });

            if (response.data.success) {
                setSuccessMessage('Response submitted successfully!');
                setReasonModalOpen(false);
                setSelectedCampusId(null);
                setNotInterestedReason('');
                // Refresh data after successful submission
                await initData();
            } else {
                throw new Error(response.data.errorMessage || 'Failed to submit response');
            }

        } catch (err) {
            console.error('Error submitting not interested:', err);
            setError(err.message || 'Failed to submit response');
        } finally {
            setNotInterestedLoading(false);
        }
    };

    const handleNotInterestedCancel = () => {
        setReasonModalOpen(false);
        setSelectedCampusId(null);
        setNotInterestedReason('');
    };

    const renderCampusTables = () => {
        if (!campusData) return null;

        return (
            <>
                {/* Online Campuses Needing Confirmation */}
                {onlineCampuses.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        {/* Header with title and alert on left, pagination on right */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography level="h4" sx={{ fontFamily: 'Proximanova Regular', mb: 0 }}>
                                    Confirm for Campus Online
                                </Typography>
                                <Alert
                                    color="info"
                                    sx={{
                                        mb: 0,
                                        py: 0.2,
                                        px: 1,
                                        fontSize: '0.875rem'
                                    }}
                                    variant="soft"
                                >
                                    You have been shortlisted based on the preferences given by you.
                                </Alert>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                                <Typography level="body-sm" sx={{ mr: 1 }}>
                                    Page {pagination.online.page} of {Math.max(1, Math.ceil((pagination.online.total || 0) / perPage))}
                                </Typography>
                                <Button
                                    size="sm"
                                    variant="outlined"
                                    onClick={() => setOnlinePage((p) => Math.max(1, p - 1))}
                                    disabled={confirming || notInterestedLoading || pagination.online.page <= 1}
                                    sx={{ borderColor: '#6e7072', color: '#6e7072', '&:hover': { borderColor: '#404041', backgroundColor: '#f5f5f5' } }}
                                >
                                    Previous
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outlined"
                                    onClick={() => setOnlinePage((p) => p + 1)}
                                    disabled={confirming || notInterestedLoading || (pagination.online.page >= Math.ceil((pagination.online.total || 0) / perPage))}
                                    sx={{ borderColor: '#6e7072', color: '#6e7072', '&:hover': { borderColor: '#404041', backgroundColor: '#f5f5f5' } }}
                                >
                                    Next
                                </Button>
                            </Box>
                        </Box>
                        <Sheet variant="outlined" sx={tableSheetSx}>
                            <Table aria-label="online campus confirmation table" sx={onlineCampusTableSx}>
                                <thead>
                                    <tr>
                                        <th>Sl. No.</th>
                                        <th>Campus Code</th>
                                        <th>Company Name</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {onlineCampuses.map((campus) => (
                                        <tr key={campus.serialNumber}>
                                            <td>
                                                <Typography level="body-md" sx={cellTextSx}>
                                                    {campus.serialNumber}
                                                </Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-md" sx={cellTextSx}>
                                                    {campus.campusCode}
                                                </Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-md" sx={cellTextSx}>
                                                    {campus.companyName || '-'}
                                                </Typography>
                                            </td>
                                            <td>
                                                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                                                    <Button
                                                        size="sm"
                                                        variant="solid"
                                                        color="primary"
                                                        onClick={() => confirmCampus(campus.campusId)}
                                                        disabled={confirming || notInterestedLoading}
                                                        sx={{
                                                            backgroundColor: '#eb7a24',
                                                            '&:hover': { backgroundColor: '#e55125' }
                                                        }}
                                                    >
                                                        {confirming ? 'Confirming...' : 'Confirm'}
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outlined"
                                                        color="neutral"
                                                        onClick={() => handleNotInterestedClick(campus.campusId)}
                                                        disabled={confirming || notInterestedLoading}
                                                        sx={{
                                                            borderColor: '#6e7072',
                                                            color: '#6e7072',
                                                            '&:hover': {
                                                                borderColor: '#404041',
                                                                backgroundColor: '#f5f5f5'
                                                            }
                                                        }}
                                                    >
                                                        Not Interested
                                                    </Button>
                                                </Stack>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Sheet>
                    </Box>
                )}
                {/* Confirmed Campuses Table */}
                {confirmedCampuses.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        {/* Header with title on left and pagination on right */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography level="h4" sx={{ fontFamily: 'Proximanova Regular' }}>
                                Campus Information
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography level="body-sm" sx={{ mr: 1 }}>
                                    Page {pagination.confirmed.page} of {Math.max(1, Math.ceil((pagination.confirmed.total || 0) / perPage))}
                                </Typography>
                                <Button
                                    size="sm"
                                    variant="outlined"
                                    onClick={() => setConfirmedPage((p) => Math.max(1, p - 1))}
                                    disabled={pagination.confirmed.page <= 1}
                                    sx={{ borderColor: '#6e7072', color: '#6e7072', '&:hover': { borderColor: '#404041', backgroundColor: '#f5f5f5' } }}
                                >
                                    Previous
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outlined"
                                    onClick={() => setConfirmedPage((p) => p + 1)}
                                    disabled={pagination.confirmed.page >= Math.ceil((pagination.confirmed.total || 0) / perPage)}
                                    sx={{ borderColor: '#6e7072', color: '#6e7072', '&:hover': { borderColor: '#404041', backgroundColor: '#f5f5f5' } }}
                                >
                                    Next
                                </Button>
                            </Box>
                        </Box>
                        <Sheet variant="outlined" sx={tableSheetSx}>
                            <Table aria-label="confirmed campus table" sx={confirmedCampusTableSx}>
                                <thead>
                                    <tr>
                                        <th>Sl. No.</th>
                                        <th>Campus Code</th>
                                        <th>Company Name</th>
                                        <th>Status</th>
                                        <th>Reason</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {confirmedCampuses.map((campus) => (
                                        <tr key={campus.serialNumber}>
                                            <td>
                                                <Typography level="body-md" sx={cellTextSx}>
                                                    {campus.serialNumber}
                                                </Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-md" sx={cellTextSx}>
                                                    {campus.campusCode}
                                                </Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-md" sx={cellTextSx}>
                                                    {campus.companyName || '-'}
                                                </Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-md" sx={{ ...cellTextSx, color: campus.statusColor, fontWeight: 'bold' }}>
                                                    {campus.status}
                                                </Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-md" sx={cellTextSx}>
                                                    {campus.reason || '-'}
                                                </Typography>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Sheet>
                    </Box>
                )}


            </>
        );
    };

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="400px"
            >
                <CircularProgress size="lg" />
                <Typography level="body-md" sx={{ ml: 2 }}>
                    Loading campus information...
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, width: '100%', minWidth: 0 }}>
            <Typography level="h3" sx={{ mb: 3, fontFamily: 'Proximanova Regular' }}>
                Campus Confirmation
            </Typography>

            {error && (
                <Alert color="danger" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {successMessage && (
                <Alert color="success" sx={{ mb: 3 }}>
                    {successMessage}
                </Alert>
            )}

            Campus Confirmation Summary
            {campusData?.summary && (
                <Card variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'background.level1' }}>
                    <Stack direction="row" spacing={4} justifyContent="center">
                        <Box textAlign="center">
                            <Typography level="body-sm" color="neutral">Total Campus</Typography>
                            <Typography level="h3" sx={{ color: '#eb7a24', fontFamily: 'Proximanova Regular' }}>
                                {campusData.summary.totalConfirmed}
                            </Typography>
                        </Box>
                        <Box textAlign="center">
                            <Typography level="body-sm" color="neutral">Pending Confirmation</Typography>
                            <Typography level="h3" sx={{ color: '#eb7a24', fontFamily: 'Proximanova Regular' }}>
                                {campusData.summary.totalPendingConfirmation}
                            </Typography>
                        </Box>
                    </Stack>
                </Card>
            )}

            {/* Campus Tables */}
            <Card variant="outlined" sx={{ p: 3, minWidth: 0 }}>
                {campusData?.message && (
                    <Alert color="info" sx={{ mb: 3 }}>
                        {campusData.message}
                    </Alert>
                )}
                {renderCampusTables()}
            </Card>

            {/* Not Interested Reason Modal */}
            <Modal open={reasonModalOpen} onClose={handleNotInterestedCancel}>
                <ModalDialog
                    aria-labelledby="not-interested-modal"
                    aria-describedby="not-interested-description"
                    sx={{ maxWidth: 500 }}
                >
                    <Typography
                        id="not-interested-modal"
                        component="h2"
                        level="h4"
                        sx={{ mb: 1 }}
                    >
                        Not Interested - Please Share Reason
                    </Typography>
                    <Typography
                        id="not-interested-description"
                        textColor="text.secondary"
                        sx={{ mb: 2 }}
                    >
                        This information helps us analyze and improve our system. Your feedback is valuable to us.
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                        <FormLabel>Reason (Optional)</FormLabel>
                        <Textarea
                            placeholder="Please share why you are not interested in this campus..."
                            value={notInterestedReason}
                            onChange={(e) => setNotInterestedReason(e.target.value)}
                            minRows={3}
                            sx={{ mt: 1 }}
                        />
                    </Box>
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button
                            variant="outlined"
                            onClick={handleNotInterestedCancel}
                            disabled={notInterestedLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="solid"
                            onClick={handleNotInterestedSubmit}
                            loading={notInterestedLoading}
                            sx={{
                                backgroundColor: '#6e7072',
                                '&:hover': { backgroundColor: '#404041' }
                            }}
                        >
                            {notInterestedLoading ? 'Submitting...' : 'Submit'}
                        </Button>
                    </Stack>
                </ModalDialog>
            </Modal>
        </Box>
    );
};

export default CampusConfirmation;